/// <amd-module name="Dashboards/DashboardView/src/redux/selectors/Selectors" />
import { DashboardScope } from "Dashboards/Common/Contracts/Dashboard";
import { TeamContext } from "Dashboards/Common/Contracts/TeamContext";
import * as DashboardPanelConstants from "Dashboards/DashboardPanel/Common";
import * as Reselect from "Reselect";
import { IProjectData, ITeamData } from "Tfs/Platform/Page";
import { ITfsPageDataAwareState } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataContracts";
import { currentProject } from "Tfs/ReduxCommon/Modules/TfsPageData/TfsPageDataSelectors";
import { IIdentity } from "VSS/Features/Identities/IdentityService";
import { IVssPageContext } from "VSS/Platform/Context";
import { isPageInteractive, ITTIAwareState } from "VSS/Redux/Modules/TTIMonitor";
import { Dashboard, DashboardOwner } from "../../model/Dashboard";
import { DashboardViewAwareState, ErrorState } from "../../model/DashboardViewState";
import { LoadingStrategy } from "../../model/LoadingStrategies";
import { PresentationMode } from "../../model/PresentationMode";
import { Widget } from "../../model/Widget";
import { WidgetMap } from "../../model/WidgetMap";
import { combineStatePiecesIntoDashboard } from "../../utilities/DashboardModelConverter";
import * as ReducerSelectors from "./ReducerSelectors";
import {
    filterUsingAssociativeArray,
    flipBooleanAssociativeArrayValues,
    isInternalWidget,
    isModernWidget,
    isSlowWidget,
    toBooleanAssociativeArray,
    widgetListComparer
} from "./SelectorHelpers";

/**
 * Represents the fraction of widgets in the visible set when the dashboard first rendered
 * that must indicate they've loaded before marking TTI.
 */
export const TTIThreshold = 0.75;

// Note: As a matter of convention, Dashboard selectors are used uniformly to abstract away interpretation on state.

// #region Basic Selectors - Simply return pieces of the store state.

export function getDashboard(state: DashboardViewAwareState): Dashboard | undefined {
    return state.dashboardViewState.dashboard;
}

/** Gets the team data of the dashboard. Returns undefined if still loading or if the dashboard is team-agnostic. */
export function getDashboardTeamData(state: DashboardViewAwareState): ITeamData | undefined {
    return state.dashboardViewState.teamData;
}

/** Gets the identity of the owner, if any, for the dashboard. */
export function getDashboardOwnerIdentity(state: DashboardViewAwareState): IIdentity | undefined {
    return state.dashboardViewState.ownerIdentity;
}

/** Gets the default team data of the project. Returns undefined if still loading. */
export function getProjectDefaultTeamData(state: DashboardViewAwareState): ITeamData | undefined {
    return state.dashboardViewState.defaultProjectTeamData;
}

export function getEmbedded(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.embedded;
}

export function getRenderTimestamp(state: DashboardViewAwareState): number {
    return state.dashboardViewState.renderTimestamp;
}

export function getCurrentTimestamp(state: DashboardViewAwareState): number {
    return state.dashboardViewState.currentTimestamp;
}

export function getDraggedWidgetId(state: DashboardViewAwareState): string | undefined {
    return state.dashboardViewState.draggedWidgetId;
}

export function getDraggedEvent(state: DashboardViewAwareState): Event | undefined {
    return state.dashboardViewState.dragEvent as Event;
}

export function getPresentationMode(state: DashboardViewAwareState): PresentationMode {
    if (state.dashboardViewState.previewWidgetId !== undefined) {
        return PresentationMode.Config;
    } else if (state.dashboardViewState.showEditMode) {
        return PresentationMode.Edit;
    } else if (state.dashboardViewState.showCopyDashboardPanel) {
        return PresentationMode.Copy;
    } else {
        return PresentationMode.View;
    }
}

export function showDashboardTemplatePage(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showDashboardTemplatePage;
}

export function showConfigMode(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showConfigMode;
}

export function showFullScreen(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showFullScreen;
}

export function showDashboardSettingsManager(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showDashboardSettingsManager;
}

export function showCopyDashboardPanel(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showCopyDashboardPanel;
}

export function getCopyDashboardProjectsList(state: DashboardViewAwareState): DashboardPanelConstants.Project[] {
    return state.dashboardViewState.copyDashboardPanelState.projects as any;
}

export function getCopyDashboardProjectTeamList(state: DashboardViewAwareState): {[id:string]: DashboardPanelConstants.TeamProjectItem} {
    return state.dashboardViewState.copyDashboardPanelState.projectTeamList as any;
}

export function getCopyDashboardDedicatedApiEnabled (state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.copyDashboardPanelState.useDedicatedApi as any;
}

export function canCreateDashboards(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.permissionsState.canCreateDashboards;
}

export function getPreviewWidgetId(state: DashboardViewAwareState): string | undefined {
    return state.dashboardViewState.previewWidgetId;
}

export function getPreviewHighlighting(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.highlightOnPreview;
}

export function getWidgetsSeenInitially(state: DashboardViewAwareState): ReadonlyArray<string> {
    return state.dashboardViewState.widgetsSeenInitially;
}

export function getWidgetsLoaded(state: DashboardViewAwareState): ReadonlyArray<string> {
    return state.dashboardViewState.widgetsLoaded;
}

export function publishedDirtyTTI(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.publishedDirtyTTI;
}

export function getErrorMessageData(state: DashboardViewAwareState): ErrorState | undefined {
    return state.dashboardViewState.errorState;
}

export function getPastWidgets(state: DashboardViewAwareState): ReadonlyArray<Widget> {
    return state.dashboardViewState.pastWidgets;
}

export function getPreviewWidgets(state: DashboardViewAwareState): ReadonlyArray<Widget> | undefined {
    return state.dashboardViewState.previewWidgets;
}

export function unpositionedWidgetWasPlaced(state: DashboardViewAwareState): boolean {
    return !!state.dashboardViewState.unpositionedWidgetWasPlaced;
}

// #endregion Basic Selectors

// #region Complex Selectors - Selectors that do calculations or use other selectors but aren't themselves memoized.

export function dashboardHasLegacyWidgets(state: DashboardViewAwareState, pageContext: IVssPageContext): boolean {
    return getWidgetIds(state).length !== getModernInternalWidgetIds(state, pageContext).length;
}

export function canEditDashboard(state: DashboardViewAwareState): boolean {
    // Prevents rendering most dashboard edit UI in embedded scenarios
    return !getEmbedded(state) && state.dashboardViewState.permissionsState.canEditDashboard;
}

export function canManagePermissionsForDashboard(state: DashboardViewAwareState): boolean {
    return !getEmbedded(state) && state.dashboardViewState.permissionsState.canManagePermissionsForDashboard;
}

export function dashboardIsInitialized(state: DashboardViewAwareState): boolean {
    return ReducerSelectors.dashboardIsInitialized(state.dashboardViewState);
}

export function getRefreshInterval(state: DashboardViewAwareState): number | never {
    // Written as a regular function instead of a memoized selector utilizing getDashboard as an input
    // because supposedly the stack trace from reselect isn't helpful when an error is thrown,
    // and because the logic here is simply extracting state. No complex logic.
    const dashboard = getDashboard(state);
    if (dashboard == null) {
        throw Error("Cannot retrieve refresh interval of undefined dashboard");
    }

    return dashboard.refreshInterval;
}

export function showRefreshTime(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.showRefreshTime;
}

export function isThrottlingWarningActive(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.isThrottlingWarningActive;
}
export function isThrottlingAllowed(state: DashboardViewAwareState): boolean {
    return state.dashboardViewState.isThrottlingAllowed;
}

export function getWidgetsSeen(state: DashboardViewAwareState): ReadonlyArray<string> {
    return ReducerSelectors.getWidgetsSeen(state.dashboardViewState);
}

export function getWidgets(state: DashboardViewAwareState): ReadonlyArray<Widget> {
    return ReducerSelectors.getWidgets(state.dashboardViewState);
}

export function getWidgetIds(state: DashboardViewAwareState): ReadonlyArray<string> {
    return ReducerSelectors.getWidgetIds(state.dashboardViewState);
}

export function getWidgetsSeenMap(state: DashboardViewAwareState): Readonly<WidgetMap<boolean>> {
    return ReducerSelectors.getWidgetsSeenMap(state.dashboardViewState);
}

// #endregion Complex Selectors

// #region Memoized Selectors - Selectors with simple caching

export const getDashboardOwner = Reselect.createSelector(
    getDashboard,
    getDashboardOwnerIdentity,
    getDashboardTeamData,
    (dashboard, ownerIdentity, ownerTeam): DashboardOwner | undefined => {
        if (dashboard != null) {
            if (dashboard.dashboardScope == DashboardScope.Project && ownerIdentity != null) {
                return { id: dashboard.ownerId, name: ownerIdentity.displayName! };
            } else if (dashboard.dashboardScope == DashboardScope.Project_Team) {
                return ownerTeam;
            }
        }

        return undefined;
    }
);

// If team data is not available, use the project's default team.
export const getTeamDataOrDefault = Reselect.createSelector(
    getDashboardTeamData,
    getProjectDefaultTeamData,
    (dashboardTeamData, projectDefaultTeamData): ITeamData | never => {
        if (dashboardTeamData == null && projectDefaultTeamData == null) {
            throw Error("No team data available in state");
        }

        return dashboardTeamData || (projectDefaultTeamData as ITeamData);
    }
);

/** Gets the "active" set of widgets. This is from preview in preview mode, otherwise it is sourced from the client side "draft" set. */
export const getActiveWidgets = Reselect.createSelector(
    showConfigMode,
    getPreviewWidgets,
    getWidgets,
    (showConfigMode, previewWidgets, widgets) => (showConfigMode ? previewWidgets : widgets) as ReadonlyArray<Widget>
);

/** Gets the team context of the dashboard. If the dashboard is team agnostic, the team information will be filled with empty strings. */
export const getDashboardTeamContext = Reselect.createSelector<
    DashboardViewAwareState & ITfsPageDataAwareState,
    ITeamData | undefined,
    IProjectData | undefined,
    TeamContext
>(
    getDashboardTeamData,
    currentProject,
    (teamData, projectData): TeamContext | never => {
        if (projectData) {
            if (teamData) {
                return {
                    project: projectData.name,
                    projectId: projectData.id,
                    team: teamData.name,
                    teamId: teamData.id
                };
            } else {
                return {
                    project: projectData.name,
                    projectId: projectData.id,
                    team: "",
                    teamId: ""
                };
            }
        } else {
            throw Error("Cannot retrieve team context with undefined project data");
        }
    }
);

export const getWidgetsSeenInitiallyMap = Reselect.createSelector(
    getWidgetsSeenInitially,
    getWidgetIds,
    toBooleanAssociativeArray
);

export const getInternalWidgets = Reselect.createSelector(
    getWidgets,
    widgets => widgets.filter(isInternalWidget) as ReadonlyArray<Widget>
);
export const getInternalWidgetIds = Reselect.createSelector(
    getInternalWidgets,
    widgets => widgets.map(w => w.id!)
);

export const getModernInternalWidgets = Reselect.createSelector(
    getInternalWidgets,
    (state: DashboardViewAwareState, pageContext: IVssPageContext) => pageContext,
    (widgets, pageContext: IVssPageContext) => {
        return widgets.filter(w => isModernWidget(w, pageContext)) as ReadonlyArray<Widget>;
    }
);
export const getModernInternalWidgetIds = Reselect.createSelector(
    getModernInternalWidgets,
    widgets => widgets.map(w => w.id!) as ReadonlyArray<string>
);

export const getModernInternalSlowWidgets = Reselect.createSelector(
    getModernInternalWidgets,
    (state: DashboardViewAwareState, pageContext: IVssPageContext) => pageContext,
    (widgets, pageContext) => {
        return widgets.filter(w => isSlowWidget(w, pageContext)) as ReadonlyArray<Widget>;
    }
);

export const getModernInternalSlowWidgetIds = Reselect.createSelector(
    getModernInternalSlowWidgets,
    widgets => widgets.map(w => w.id!) as ReadonlyArray<string>
);

export const getLegacyInternalWidgets = Reselect.createSelector(
    getInternalWidgets,
    (state: DashboardViewAwareState, pageContext: IVssPageContext) => pageContext,
    (widgets, pageContext: IVssPageContext) => {
        return widgets.filter(w => !isModernWidget(w, pageContext)) as ReadonlyArray<Widget>;
    }
);

export const getLegacyInternalWidgetIds = Reselect.createSelector(
    getLegacyInternalWidgets,
    widgets => widgets.map(w => w.id!)
);

export const getInternalWidgetsMap = Reselect.createSelector(
    getInternalWidgetIds,
    getWidgetIds,
    toBooleanAssociativeArray
);

export const getInternalWidgetsSeenInitially = Reselect.createSelector(
    getWidgetsSeenInitially,
    getInternalWidgetsMap,
    filterUsingAssociativeArray
);

export const getInternalWidgetsLoadedAndSeenInitially = Reselect.createSelector(
    getWidgetsLoaded,
    getWidgetsSeenInitiallyMap,
    getInternalWidgetsMap,
    (widgetsLoaded, widgetsSeenInitiallyMap, internalWidgetsMap) => {
        const widgetsLoadedAndSeenInitially = filterUsingAssociativeArray(widgetsLoaded, widgetsSeenInitiallyMap);
        return filterUsingAssociativeArray(widgetsLoadedAndSeenInitially, internalWidgetsMap);
    }
);

export const getModernInternalWidgetsSeenMap = Reselect.createSelector(
    getModernInternalWidgetIds,
    getWidgetsSeen,
    toBooleanAssociativeArray
);

export const getModernInternalWidgetsSeenInitially = Reselect.createSelector(
    getModernInternalWidgetIds,
    getWidgetsSeenInitiallyMap,
    filterUsingAssociativeArray
);

export const getModernInternalSlowWidgetsSeenMap = Reselect.createSelector(
    getModernInternalSlowWidgetIds,
    getWidgetsSeen,
    toBooleanAssociativeArray
);

export const getModernInternalSlowWidgetsSeenInitially = Reselect.createSelector(
    getModernInternalSlowWidgetIds,
    getWidgetsSeenInitiallyMap,
    filterUsingAssociativeArray
);

export const getLegacyInternalWidgetsSeenMap = Reselect.createSelector(
    getLegacyInternalWidgetIds,
    getWidgetsSeen,
    toBooleanAssociativeArray
);

export const getLegacyInternalWidgetsSeenInitially = Reselect.createSelector(
    getLegacyInternalWidgetIds,
    getWidgetsSeenInitiallyMap,
    filterUsingAssociativeArray
);

export const getLoadingStrategies = Reselect.createSelector<
    DashboardViewAwareState & ITTIAwareState,
    IVssPageContext,
    boolean,
    boolean,
    ReadonlyArray<string>,
    ReadonlyArray<string>,
    ReadonlyArray<string>,
    ReadonlyArray<LoadingStrategy>
>(
    isPageInteractive,
    publishedDirtyTTI,
    getInternalWidgetsSeenInitially,
    getModernInternalWidgetsSeenInitially,
    getModernInternalSlowWidgetsSeenInitially,
    (
        isPageInteractive,
        publishedDirtyTTI,
        internalWidgetsSeenInitially,
        modernInternalWidgetsSeenInitially,
        modernInternalSlowWidgetsSeenInitially
    ): ReadonlyArray<LoadingStrategy> => {
        const strategies: LoadingStrategy[] = [];
        const thresholdNumber = TTIThreshold * internalWidgetsSeenInitially.length;

        if (isPageInteractive || publishedDirtyTTI) {
            /* If we've already marked TTI then applying a loading strategy doesn't provide any benefit */
            return [LoadingStrategy.Immediate];
        }

        if (modernInternalWidgetsSeenInitially.length >= thresholdNumber) {
            strategies.push(LoadingStrategy.DeferLegacyWidgets);
        }

        /** If it is possible to delay slow modern widgets, check if we can apply a more aggressive strategy */
        if (modernInternalSlowWidgetsSeenInitially.length > 0) {
            if (internalWidgetsSeenInitially.length - modernInternalSlowWidgetsSeenInitially.length >= thresholdNumber) {
                strategies.push(LoadingStrategy.DeferSlowModernWidgets);
            }

            if (modernInternalWidgetsSeenInitially.length - modernInternalSlowWidgetsSeenInitially.length >= thresholdNumber) {
                strategies.push(LoadingStrategy.DeferLegacyAndSlowModernWidgets);
            }
        }

        if (strategies.length === 0) {
            /* If none of the above strategies can be applied then we need to load all widgets immediately in order to meet TTI */
            return [LoadingStrategy.Immediate];
        }

        return strategies;
    }
);

export const getBestLoadingStrategy = Reselect.createSelector(
    getLoadingStrategies,
    getModernInternalSlowWidgetsSeenInitially,
    getLegacyInternalWidgetsSeenInitially,
    (strategies, modernInternalSlowWidgetsSeenInitially, legacyInternalWidgetsSeenInitially): Readonly<LoadingStrategy> => {
        if (strategies.length === 1) {
            return strategies[0];
        }

        if (strategies.some(strategy => strategy === LoadingStrategy.DeferLegacyAndSlowModernWidgets)) {
            return LoadingStrategy.DeferLegacyAndSlowModernWidgets;
        }

        return modernInternalSlowWidgetsSeenInitially.length > legacyInternalWidgetsSeenInitially.length
            ? LoadingStrategy.DeferSlowModernWidgets
            : LoadingStrategy.DeferLegacyWidgets;
    }
);

export const showLoading = Reselect.createSelector(
    getDashboard,
    getDashboardOwnerIdentity,
    getDashboardTeamData,
    getProjectDefaultTeamData,
    (dashboard, ownerIdentity, teamData, defaultTeamData): boolean => {
        return dashboard == null || ownerIdentity == null || (teamData == null && defaultTeamData == null);
    }
);

export const showThrottlingWarning = Reselect.createSelector(
    isThrottlingWarningActive,
    isThrottlingAllowed,
    getRefreshInterval,
    (isThrottlingWarningActive, isThrottlingAllowed, refreshInterval): boolean => {
        return isThrottlingWarningActive && isThrottlingAllowed && refreshInterval > 0;
    }
);

export const userScrolledMoreWidgetsIntoViewport = Reselect.createSelector(
    getWidgetsSeen,
    getWidgetsSeenInitially,
    (widgetsSeen, widgetsSeenInitially): boolean => widgetsSeen.length > widgetsSeenInitially.length
);

export const shouldOmitTTI = userScrolledMoreWidgetsIntoViewport;

/**
 * Returns true if there are 0 internal widgets,
 * or 75% of the internal widgets have indicated that they are loaded.
 * @param state The UI state of the dashboard
 */
export const shouldMarkTTI = Reselect.createSelector<
    DashboardViewAwareState & ITTIAwareState,
    boolean,
    boolean,
    boolean,
    ReadonlyArray<string>,
    ReadonlyArray<string>,
    boolean
>(
    isPageInteractive,
    publishedDirtyTTI,
    dashboardIsInitialized,
    getInternalWidgetsSeenInitially,
    getInternalWidgetsLoadedAndSeenInitially,
    (markedTTI, publishedDirtyTTI, dashboardIsInitialized, internalWidgetsSeenInitially, internalWidgetsLoadedAndSeenInitially): boolean =>
        !markedTTI &&
        !publishedDirtyTTI &&
        dashboardIsInitialized &&
        internalWidgetsLoadedAndSeenInitially.length >= internalWidgetsSeenInitially.length * TTIThreshold
);

export const getDeferredWidgetsMap = Reselect.createSelector(
    getBestLoadingStrategy,
    getLegacyInternalWidgetIds,
    getModernInternalSlowWidgetIds,
    getWidgetIds,
    (strategy, legacyInternalWidgetIds, modernInternalSlowWidgetIds, widgetIds): WidgetMap<boolean> => {
        switch (strategy) {
            default:
            case LoadingStrategy.Immediate:
                return toBooleanAssociativeArray([], widgetIds);
            case LoadingStrategy.DeferLegacyWidgets:
                return toBooleanAssociativeArray(legacyInternalWidgetIds, widgetIds);
            case LoadingStrategy.DeferSlowModernWidgets:
                return toBooleanAssociativeArray(modernInternalSlowWidgetIds, widgetIds);
            case LoadingStrategy.DeferLegacyAndSlowModernWidgets:
                return toBooleanAssociativeArray(legacyInternalWidgetIds.concat(modernInternalSlowWidgetIds), widgetIds);
        }
    }
);

export const getShouldRenderWidgetMap = Reselect.createSelector(
    getDeferredWidgetsMap,
    getWidgetsSeen,
    (deferredWidgetsMap, widgetsSeen): WidgetMap<boolean> => {
        const shouldRenderWidgetIds = filterUsingAssociativeArray(widgetsSeen, flipBooleanAssociativeArrayValues(deferredWidgetsMap));
        return toBooleanAssociativeArray(shouldRenderWidgetIds, widgetsSeen);
    }
);

export const widgetsChanged = Reselect.createSelector(
    getPastWidgets,
    getWidgets,
    (pastWidgets, widgets) => !widgetListComparer(pastWidgets, widgets)
);

export const packServerDashboard = Reselect.createSelector(
    getDashboard,
    getWidgets,
    (dashboard, widgets) => {
        if (dashboard != null) {
            return combineStatePiecesIntoDashboard(dashboard, widgets);
        } else {
            throw Error("Cannot pack undefined dashboard");
        }
    }
);

export const getPreviewWidgetConfig = Reselect.createSelector(
    getPreviewWidgetId,
    getPreviewWidgets,
    (widgetId: string, widgets: ReadonlyArray<Widget>): Widget | undefined => {
        const previewWidget: Widget | undefined =
            widgets != null
                ? widgets.find(item => {
                      return item.id === widgetId;
                  })
                : undefined;
        return previewWidget != null ? previewWidget : undefined;
    }
);

// #endregion Memoized Selectors
